import React, { useEffect, useState } from 'react';
import { resolveStorageImageUrl } from '../utils/storageImageUrl';

interface StorageImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** The stored value from the database — either a plain S3 key or a legacy bare S3 URL. */
  storageKey: string | null | undefined;
  /** Rendered in place of the <img> while resolving, or if resolution fails/there is nothing to show. Defaults to null (renders nothing). */
  fallback?: React.ReactNode;
}

/**
 * Drop-in replacement for <img src={someStoredValue} /> wherever the stored
 * value is an Event.imageUrl / Announcement.imageUrl / Sponsor.logoUrl-style
 * field. Resolves the stored key/legacy-URL to a real, working presigned
 * URL via resolveStorageImageUrl() before rendering — see storageImageUrl.ts
 * for the full background on why a plain <img src={rawStoredValue}> does
 * not work for these guest-readable-but-IAM-gated S3 paths.
 */
const StorageImage: React.FC<StorageImageProps> = ({ storageKey, fallback = null, alt, ...imgProps }) => {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setResolvedUrl(null);

    if (!storageKey) return;

    resolveStorageImageUrl(storageKey).then((url) => {
      if (cancelled) return;
      if (url) {
        setResolvedUrl(url);
      } else {
        setFailed(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [storageKey]);

  if (!storageKey || failed || !resolvedUrl) {
    return <>{fallback}</>;
  }

  return (
    <img
      src={resolvedUrl}
      alt={alt}
      onError={() => {
        // Browsers do not print a failed <img> network request to the Console
        // tab by default (only the Network tab shows it, as a red row) — so
        // without this log, a 403/404/CORS failure on the actual GET to S3 is
        // functionally silent even though getUrl() succeeded. Log the URL
        // (safe: it is a short-lived presigned URL, not a credential) so this
        // failure mode is diagnosable from the Console alone next time.
        console.error(`Image failed to load (resolved URL did not succeed): ${resolvedUrl}`);
        setFailed(true);
      }}
      {...imgProps}
    />
  );
};

export default StorageImage;
